import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { ArrowDown, ArrowUp, ChevronRight, Download, Trash2, X } from "lucide-react";
import { clsx } from "clsx";
import { Button, Field, Input } from "../ui";
import { SettingsSection, StatusText } from "./SettingsSection";
import { useSession } from "./session";
import { ButtonStepEditor } from "./ButtonStepEditor";
import {
  MAX_STEPS,
  describeButton,
  describeStep,
  stepBody,
  type ButtonInfo,
  type StepInfo,
} from "./buttonSteps";
import { downloadQr } from "./buttonQr";

/**
 * Settings → Buttons (I7): private addresses that do one or more things in order — mark a chore done, start a
 * timer, add a task or a line to the tray, change a habit for today (docs/plans/habit-buttons.md), each step in a
 * workspace of its own (docs/plans/buttons-and-place-lists.md). Put one on an NFC sticker by the washing machine,
 * in a Home Assistant `rest_command`, in a bookmark, or print its QR code. Also how to show Drang in Home
 * Assistant (a read-only token and a REST sensor).
 */
export function ButtonsSettings() {
  const { t, i18n } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const allWorkspaces = useSession((s) => s.session?.workspaces);
  const workspaces = useMemo(
    () => (allWorkspaces ?? []).filter((w) => w.role !== "viewer").map((w) => ({ id: w.id, name: w.name })),
    [allWorkspaces],
  );
  const [buttons, setButtons] = useState<ButtonInfo[]>([]);
  // the button being made or changed: its steps so far, its name, and which one (null: a new one)
  const [steps, setSteps] = useState<StepInfo[]>([]);
  const [label, setLabel] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  // mounting the step editor again starts it over
  const [editorKey, setEditorKey] = useState(0);
  const [qr, setQr] = useState<{ id: number; src: string } | null>(null);
  // the Home Assistant how-to: folded until opened
  const [haOpen, setHaOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = workspaceId ? `?workspace=${encodeURIComponent(workspaceId)}` : "";

  const load = () =>
    fetch(`/api/internal/buttons${q}`, { credentials: "same-origin" }).then((r) =>
      r.ok ? (r.json() as Promise<{ buttons: ButtonInfo[] }>) : null,
    );
  useEffect(() => {
    if (!workspaceId) return;
    void load().then((d) => d && setButtons(d.buttons));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const reset = () => {
    setSteps([]);
    setLabel("");
    setEditing(null);
    setEditorKey((k) => k + 1);
  };

  /** Make the button, or save the one being changed; a complete step still in the editor goes with it. */
  const save = async (current: StepInfo | null) => {
    setError(null);
    const all = current ? [...steps, current] : steps;
    const r = await fetch(
      editing === null ? `/api/internal/buttons${q}` : `/api/internal/buttons/${editing}${q}`,
      {
        method: editing === null ? "POST" : "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ steps: all.map(stepBody), label }),
      },
    );
    const body = (await r.json()) as ButtonInfo & { error?: string };
    if (!r.ok) {
      setError(t(`errors.${body.error}`, { defaultValue: t("error.generic") }));
      return;
    }
    setButtons((b) => (editing === null ? [...b, body] : b.map((x) => (x.id === body.id ? body : x))));
    reset();
  };

  const change = (b: ButtonInfo) => {
    setSteps(b.steps);
    setLabel(b.label);
    setEditing(b.id);
    setError(null);
    setEditorKey((k) => k + 1);
  };

  const move = (i: number, by: -1 | 1) =>
    setSteps((cur) => {
      const next = [...cur];
      const [s] = next.splice(i, 1);
      next.splice(i + by, 0, s!);
      return next;
    });

  const remove = async (id: number) => {
    const r = await fetch(`/api/internal/buttons/${id}${q}`, {
      method: "DELETE",
      credentials: "same-origin",
    });
    if (r.ok) setButtons((b) => b.filter((x) => x.id !== id));
    if (editing === id) reset();
  };

  const showQr = async (b: ButtonInfo) =>
    setQr(
      qr?.id === b.id ? null : { id: b.id, src: await QRCode.toDataURL(b.url, { margin: 1, width: 200 }) },
    );

  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 });
  const fmt = (n: number) => nf.format(n);
  const describe = (b: ButtonInfo) => describeButton(t, fmt, b);
  const title = (b: ButtonInfo) => b.label || describe(b);
  const editedName = buttons.find((b) => b.id === editing);
  const origin = location.origin;
  const yaml = `rest:
  - resource: ${origin}/api/v1/ha/state
    headers:
      Authorization: !secret drang_token
    scan_interval: 60
    sensor:
      - name: Drang due today
        value_template: "{{ value_json.dueToday }}"
      - name: Drang next chore
        value_template: "{{ value_json.nextChore.title if value_json.nextChore else '' }}"
        json_attributes_path: "$.nextChore"
        json_attributes: [want, level, turn]
      - name: Drang water today
        value_template: "{{ (value_json.habits | selectattr('title', 'eq', 'Water') | first).today }}"`;

  if (!workspaceId) return null;
  return (
    <SettingsSection
      id="settings-buttons"
      intro={t("buttons.hint")}
      summary={t("settings.summary.buttons")}
      details={
        <>
          <p>{t("buttons.stepsHint")}</p>
          <p>{t("buttons.private")}</p>
        </>
      }
      title={t("buttons.title")}
      testId="settings-buttons"
      folded
    >
      <h3
        className="text-[12px] font-semibold text-neutral-600 dark:text-neutral-300"
        data-testid="buttons-new-title"
      >
        {t("buttons.newTitle")}
      </h3>
      <div className="flex flex-col gap-3" data-testid="buttons-new">
        {editedName && (
          <p className="text-[13px] font-medium" data-testid="button-editing">
            {t("buttons.editing", { name: title(editedName) })}
          </p>
        )}
        {steps.length > 0 && (
          <ol className="flex flex-col gap-1" aria-label={t("buttons.steps")} data-testid="button-steps">
            {steps.map((s, i) => (
              <li
                key={i}
                className="flex items-center gap-2 rounded-md bg-[var(--surface-2)] px-2 py-1 text-[13px]"
                data-testid="button-step"
              >
                <span className="w-5 shrink-0 text-right text-neutral-500">{i + 1}.</span>
                <span className="min-w-0 flex-1">{describeStep(t, fmt, s, workspaceId ?? "")}</span>
                <button
                  type="button"
                  aria-label={t("buttons.moveUp")}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  className="grid h-6 w-6 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-neutral-800"
                >
                  <ArrowUp size={13} aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={t("buttons.moveDown")}
                  disabled={i === steps.length - 1}
                  onClick={() => move(i, 1)}
                  className="grid h-6 w-6 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-neutral-800"
                >
                  <ArrowDown size={13} aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={t("buttons.removeStep")}
                  onClick={() => setSteps((cur) => cur.filter((_, j) => j !== i))}
                  className="grid h-6 w-6 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  data-testid="button-step-remove"
                >
                  <X size={13} aria-hidden />
                </button>
              </li>
            ))}
          </ol>
        )}
        <ButtonStepEditor key={editorKey} workspaces={workspaces} home={workspaceId}>
          {(current) => (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t("buttons.label")} htmlFor="button-label">
                <Input
                  id="button-label"
                  value={label}
                  placeholder={t("buttons.labelPlaceholder")}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </Field>
              <div className="flex flex-wrap items-end gap-2">
                <Button
                  variant="secondary"
                  disabled={!current || steps.length >= MAX_STEPS - 1}
                  onClick={() => {
                    if (!current) return;
                    setSteps((cur) => [...cur, current]);
                    setEditorKey((k) => k + 1);
                  }}
                  data-testid="button-add-step"
                >
                  {t("buttons.addStep")}
                </Button>
                <Button
                  disabled={steps.length + (current ? 1 : 0) === 0}
                  onClick={() => void save(current)}
                  data-testid="button-create"
                >
                  {editing === null ? t("buttons.create") : t("buttons.save")}
                </Button>
                {(editing !== null || steps.length > 0) && (
                  <Button variant="ghost" onClick={reset} data-testid="button-cancel">
                    {t("buttons.cancel")}
                  </Button>
                )}
              </div>
            </div>
          )}
        </ButtonStepEditor>
      </div>

      {error && <StatusText tone="err">{error}</StatusText>}

      <hr className="border-[var(--line)]" />

      <h3 className="text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
        {t("buttons.existingTitle")}
      </h3>
      {buttons.length === 0 && <p className="text-[12.5px] text-neutral-500">{t("buttons.noneYet")}</p>}
      {buttons.length > 0 && (
        <ul
          className="flex flex-col divide-y divide-neutral-100 dark:divide-neutral-800"
          data-testid="buttons-list"
        >
          {buttons.map((b) => (
            <li key={b.id} className="flex flex-col gap-1.5 py-2" data-testid="button-row">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{title(b)}</span>
                {b.label && <span className="text-[12.5px] text-neutral-500">{describe(b)}</span>}
                <StatusText tone="quiet">
                  {b.lastUsedAt
                    ? t("buttons.lastUsed", { when: new Date(b.lastUsedAt).toLocaleString() })
                    : t("buttons.neverUsed")}
                </StatusText>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => change(b)}
                  data-testid="button-change"
                >
                  {t("buttons.change")}
                </Button>
                <button
                  type="button"
                  aria-label={t("buttons.delete")}
                  onClick={() => void remove(b.id)}
                  className="grid h-7 w-7 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  compact
                  readOnly
                  value={b.url}
                  aria-label={t("buttons.address")}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 font-mono"
                  data-testid="button-url"
                />
                <Button size="sm" variant="secondary" onClick={() => void showQr(b)}>
                  {t("buttons.qr")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void downloadQr(b.url, title(b))}
                  data-testid="button-qr-download"
                >
                  <Download size={14} aria-hidden />
                  {t("buttons.qrDownload")}
                </Button>
              </div>
              {qr?.id === b.id && (
                <img
                  src={qr.src}
                  alt={t("buttons.qrAlt")}
                  width={140}
                  height={140}
                  className="rounded-sm bg-white p-1"
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* how to show Drang in Home Assistant: folded until opened */}
      <button
        type="button"
        aria-expanded={haOpen}
        onClick={() => setHaOpen((v) => !v)}
        className="flex w-fit items-center gap-1.5 text-left text-[12px] font-semibold text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100"
        data-testid="buttons-ha-toggle"
      >
        <ChevronRight
          size={13}
          aria-hidden
          className={clsx(
            "shrink-0 transition-transform motion-reduce:transition-none",
            haOpen && "rotate-90",
          )}
        />
        {t("buttons.haTitle")}
      </button>
      {haOpen && (
        <div className="flex flex-col gap-3" data-testid="buttons-ha">
          <ol className="list-decimal pl-5 text-[13px] leading-relaxed">
            <li>{t("buttons.ha1")}</li>
            <li>{t("buttons.ha2")}</li>
            <li>{t("buttons.ha3")}</li>
          </ol>
          <pre
            className="overflow-x-auto rounded-md bg-[var(--surface-2)] p-3 font-mono text-[12px]"
            data-testid="buttons-ha-yaml"
          >
            {yaml}
          </pre>
          <p className="text-[12px] text-neutral-500">{t("buttons.haTags")}</p>
        </div>
      )}
    </SettingsSection>
  );
}
